import { useState } from 'react';
import { toast } from 'sonner';
import { MessageSquareWarning, Loader2, Check, Flag } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { fmtUKDateTime } from './inboxFormat';

/**
 * Flag / view / resolve a supplier query on an invoice. Flagging never affects
 * posting — the invoice still posts to the ledger as normal; it simply also
 * sits in the Queries pool until it's marked dealt with. Works for invoices
 * being processed and for ones already posted (read-only).
 *
 * `onUpdated(item)` patches just the parent's query state (no form reset);
 * `onChanged()` refreshes the surrounding list.
 */
export function SupplierQueryPanel({ api, item, onUpdated, onChanged }) {
  const q = item?.query;
  const open = q?.status === 'open';
  const [flagging, setFlagging] = useState(false);
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resNote, setResNote] = useState('');
  const [busy, setBusy] = useState(false);

  const flag = async () => {
    if (note.trim().length < 3) { toast.error('Add a short note explaining the query'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/query`, { note });
      toast.success('Flagged for supplier query');
      setFlagging(false); setNote('');
      onUpdated?.(r.data); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not flag'); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/query/resolve`, { note: resNote });
      toast.success('Query marked dealt with');
      setResolving(false); setResNote('');
      onUpdated?.(r.data); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setBusy(false); }
  };

  const removeFlag = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/cost-inbox/${item.id}/query`);
      toast.success('Query flag removed');
      onUpdated?.(r.data); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
    finally { setBusy(false); }
  };

  if (open) {
    return (
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="inbox-query-panel">
        <div className="flex items-start gap-2">
          <MessageSquareWarning className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-amber-200">Flagged for supplier query</div>
            <div className="text-xs text-amber-100/90 mt-0.5 whitespace-pre-wrap break-words" data-testid="inbox-query-note">{q.note}</div>
            <div className="text-[11px] text-amber-200/70 mt-1">Flagged {fmtUKDateTime(q.flagged_at)}{q.flagged_by_name ? ` by ${q.flagged_by_name}` : ''}</div>
            {!resolving ? (
              <div className="flex items-center gap-3 mt-2">
                <Button size="sm" onClick={() => setResolving(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inbox-query-resolve-open">
                  <Check className="w-3.5 h-3.5 mr-1" /> Mark dealt with
                </Button>
                <button type="button" onClick={removeFlag} disabled={busy} className="text-xs text-amber-200/70 hover:text-amber-100 underline disabled:opacity-50" data-testid="inbox-query-remove">Remove flag</button>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <Textarea value={resNote} onChange={e => setResNote(e.target.value)} className="bg-[#1E2530] border-white/20 text-white min-h-[56px] text-sm" placeholder="Outcome (optional) — e.g. credit note agreed" data-testid="inbox-query-resolution-note" />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setResolving(false)}>Cancel</Button>
                  <Button size="sm" onClick={resolve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inbox-query-resolve-confirm">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Confirm dealt with
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid="inbox-query-panel">
      {q?.status === 'resolved' && (
        <div className="text-[11px] text-emerald-300/80 mb-2" data-testid="inbox-query-resolved-note">
          Previous query dealt with {fmtUKDateTime(q.resolved_at)}{q.resolved_by_name ? ` by ${q.resolved_by_name}` : ''}{q.resolution_note ? ` — ${q.resolution_note}` : ''}
        </div>
      )}
      {!flagging ? (
        <button type="button" onClick={() => setFlagging(true)} className="inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200" data-testid="inbox-query-flag-open">
          <Flag className="w-4 h-4" /> Flag for supplier query
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-gray-300 font-medium">Flag for supplier query</div>
          <div className="text-[11px] text-gray-500">Posts to the ledger as normal, but keeps this invoice in the Queries list to raise with the supplier.</div>
          <Textarea value={note} onChange={e => setNote(e.target.value)} className="bg-[#1E2530] border-white/20 text-white min-h-[56px] text-sm" placeholder="Why is this being queried? (e.g. price higher than quoted, wrong item)" data-testid="inbox-query-note-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setFlagging(false); setNote(''); }}>Cancel</Button>
            <Button size="sm" onClick={flag} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-[#1E2530] font-semibold" data-testid="inbox-query-flag-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Flag className="w-3.5 h-3.5 mr-1" />} Flag query
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
